import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Play, Pause } from "lucide-react";

const GALLERY_DOORS = [
  // Toscana series (page 24) — natural wood veneer
  { id: "t01", series: "Toscana", name: "Chevron Wood", caption: "Hand-laid chevron pattern in natural oak.", image: "/pdf-embed/atelier-toscana-01-chevron-wood.jpg" },
  { id: "t02", series: "Toscana", name: "Minimal Oak", caption: "Clean vertical grain, long black handle.", image: "/pdf-embed/atelier-toscana-02-minimal-oak.jpg" },
  { id: "t03", series: "Toscana", name: "Vertical Walnut", caption: "Solid walnut with vertical figuring.", image: "/pdf-embed/atelier-toscana-03-vertical-walnut.jpg" },
  { id: "t04", series: "Toscana", name: "Horizontal with Glass", caption: "Horizontal oak plank with slim glass reveal.", image: "/pdf-embed/atelier-toscana-04-horizontal-glass.jpg" },
  { id: "t05", series: "Toscana", name: "Fluted Modern", caption: "Continuous vertical fluting, black surround.", image: "/pdf-embed/atelier-toscana-05-fluted-modern.jpg" },
  { id: "t06", series: "Toscana", name: "Dark Horizontal", caption: "Dark oak with wide horizontal panels.", image: "/pdf-embed/atelier-toscana-06-dark-horizontal.jpg" },

  // Monaco series (page 25) — aluminum-clad walnut
  { id: "m01", series: "Monaco", name: "Wooden Cube", caption: "Aluminum-clad walnut, geometric cube layout.", image: "/pdf-embed/atelier-monaco-01-wooden-cube.jpg" },
  { id: "m02", series: "Monaco", name: "Double Swing Vertical Oak", caption: "Double pivot doors with light oak center band.", image: "/pdf-embed/atelier-monaco-02-double-swing-vertical-oak.jpg" },
  { id: "m03", series: "Monaco", name: "Matte Black Diamond", caption: "Matte black door with etched diamond lines.", image: "/pdf-embed/atelier-monaco-03-matte-black-diamond.jpg" },
  { id: "m04", series: "Monaco", name: "Dark Walnut Glass Inlay", caption: "Dark walnut pivot with slim glass inlay.", image: "/pdf-embed/atelier-monaco-04-dark-walnut-glass-inlay.jpg" },
  { id: "m05", series: "Monaco", name: "Horizontal Groove Light Oak", caption: "Light oak pivot with sculpted horizontal grooves.", image: "/pdf-embed/atelier-monaco-05-horizontal-groove-light-oak.jpg" },
  { id: "m06", series: "Monaco", name: "Diamond with Side Lights", caption: "Black geometric diamond with full-height side lights.", image: "/pdf-embed/atelier-monaco-06-geometric-diamond-side-lights.jpg" },

  // Milano series (page 26) — melamine finish
  { id: "i01", series: "Milano", name: "Linear Oak with Black Accents", caption: "Light oak with vertical black inlay stripes.", image: "/pdf-embed/atelier-milano-01-linear-oak-black-accents.jpg" },
  { id: "i02", series: "Milano", name: "Horizontal Walnut with Black Insert", caption: "Warm walnut with matte black horizontal insert.", image: "/pdf-embed/atelier-milano-02-horizontal-walnut-black-insert.jpg" },
  { id: "i03", series: "Milano", name: "Sculpted Dark Oak", caption: "Dark oak with subtle recessed detail.", image: "/pdf-embed/atelier-milano-03-sculpted-dark-oak.jpg" },
  { id: "i04", series: "Milano", name: "Modern Oak with Vertical Glass", caption: "Light oak plank with vertical glass reveal.", image: "/pdf-embed/atelier-milano-04-modern-oak-vertical-glass.jpg" },
  { id: "i05", series: "Milano", name: "Clean White Oak", caption: "Whitewashed oak with slim black handle.", image: "/pdf-embed/atelier-milano-05-clean-white-oak.jpg" },
  { id: "i06", series: "Milano", name: "Natural Walnut with Metal Handle", caption: "Deep walnut with polished metal pull.", image: "/pdf-embed/atelier-milano-06-natural-walnut-metal-handle.jpg" },
];

export default function GallerySection() {
  const [active, setActive] = useState(0);
  const [direction, setDirection] = useState(1);
  const [playing, setPlaying] = useState(true);
  const [hovering, setHovering] = useState(false);
  const thumbStripRef = useRef(null);

  const total = GALLERY_DOORS.length;
  const door = GALLERY_DOORS[active];
  const AUTOPLAY_MS = 4500;

  const go = useCallback((delta) => {
    setDirection(delta);
    setActive((prev) => (prev + delta + total) % total);
  }, [total]);

  const jumpTo = (index) => {
    setDirection(index > active ? 1 : -1);
    setActive(index);
  };

  // Autoplay: advance every AUTOPLAY_MS, pause when user is hovering the viewer
  useEffect(() => {
    if (!playing || hovering) return;
    const id = setInterval(() => {
      setDirection(1);
      setActive((prev) => (prev + 1) % total);
    }, AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [playing, hovering, total]);

  // Preload adjacent images
  useEffect(() => {
    [1, -1].forEach((d) => {
      const idx = (active + d + total) % total;
      const img = new Image();
      img.src = GALLERY_DOORS[idx].image;
    });
  }, [active, total]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go]);

  // Auto-scroll active thumbnail horizontally within the strip only.
  // We intentionally avoid `scrollIntoView` — it also scrolls the page vertically
  // and would yank the viewport back to the gallery on every autoplay tick.
  useEffect(() => {
    const strip = thumbStripRef.current;
    if (!strip) return;
    const el = strip.querySelector(`[data-thumb-index="${active}"]`);
    if (!el) return;
    const target =
      el.offsetLeft - strip.clientWidth / 2 + el.clientWidth / 2;
    strip.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
  }, [active]);

  return (
    <section
      id="gallery"
      className="relative bg-brand-bone py-24 md:py-32 overflow-hidden"
      data-testid="gallery-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        {/* Heading */}
        <div className="text-center mb-4">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="text-brand-green font-light uppercase tracking-[0.06em] text-3xl md:text-4xl lg:text-5xl"
            data-testid="gallery-title"
          >
            Explore Designs by Atelier
          </motion.h2>
          <div className="mx-auto w-24 h-[3px] bg-brand-gold mt-5" />
        </div>
        <p className="max-w-3xl mx-auto text-center text-brand-ink/75 leading-relaxed font-light mb-12 md:mb-16">
          Eighteen signature entries across our Toscana, Monaco and Milano series — every
          design available in the finishes, panel styles and dimensions of your project.
        </p>

        {/* Big hero viewer */}
        <div className="relative">
          <div
            className="relative w-full aspect-[16/10] md:aspect-[16/9] bg-brand-green overflow-hidden"
            data-testid="gallery-viewer"
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
          >
            <AnimatePresence initial={false} custom={direction} mode="wait">
              <motion.img
                key={door.id}
                src={door.image}
                alt={`${door.name} — ${door.series} Series`}
                custom={direction}
                initial={{ opacity: 0, x: direction * 60 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -direction * 60 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0 w-full h-full object-contain"
                data-testid={`gallery-active-image-${door.id}`}
              />
            </AnimatePresence>

            {/* Bottom gradient + label */}
            <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/75 via-black/40 to-transparent pointer-events-none" />

            <div className="absolute top-6 left-6 right-6 md:top-8 md:left-10 md:right-10 flex items-center justify-between text-white/90">
              <span className="text-[0.62rem] tracking-[0.28em] uppercase font-medium">
                {door.series} Series
              </span>
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => setPlaying((p) => !p)}
                  aria-label={playing ? "Pause autoplay" : "Play autoplay"}
                  data-testid="gallery-playpause"
                  className="flex items-center gap-1.5 text-[0.62rem] tracking-[0.28em] uppercase font-medium hover:text-brand-gold transition-colors"
                >
                  {playing ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                  {playing ? "Auto" : "Paused"}
                </button>
                <span
                  className="text-[0.62rem] tracking-[0.28em] uppercase font-medium"
                  data-testid="gallery-counter"
                >
                  {String(active + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
                </span>
              </div>
            </div>

            <div className="absolute inset-x-0 bottom-0 p-4 md:p-10 text-white">
              <div className="w-10 h-[2px] bg-brand-gold mb-3 md:mb-4" />
              <div className="min-w-0">
                <h3
                  className="text-base sm:text-lg md:text-4xl font-light tracking-[0.02em] uppercase leading-tight break-words"
                  data-testid="gallery-active-name"
                >
                  {door.name}
                </h3>
                <p className="mt-1.5 md:mt-2 text-[0.7rem] sm:text-xs md:text-sm text-white/80 leading-snug md:leading-relaxed font-light max-w-md line-clamp-2 md:line-clamp-none">
                  {door.caption}
                </p>
              </div>
            </div>

            {/* Autoplay progress bar */}
            {playing && !hovering && (
              <motion.div
                key={`bar-${active}`}
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: AUTOPLAY_MS / 1000, ease: "linear" }}
                className="absolute bottom-0 left-0 h-[3px] w-full bg-brand-gold origin-left"
                data-testid="gallery-progress"
              />
            )}

            {/* Prev / Next */}
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Previous design"
              data-testid="gallery-prev"
              className="absolute left-3 md:left-6 top-[38%] md:top-1/2 -translate-y-1/2 w-10 h-10 md:w-14 md:h-14 rounded-full bg-white/15 hover:bg-white/30 backdrop-blur-md border border-white/25 text-white flex items-center justify-center transition-all duration-300 hover:scale-105"
            >
              <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Next design"
              data-testid="gallery-next"
              className="absolute right-3 md:right-6 top-[38%] md:top-1/2 -translate-y-1/2 w-10 h-10 md:w-14 md:h-14 rounded-full bg-white/15 hover:bg-white/30 backdrop-blur-md border border-white/25 text-white flex items-center justify-center transition-all duration-300 hover:scale-105"
            >
              <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
            </button>
          </div>

          {/* Thumbnail strip */}
          <div
            ref={thumbStripRef}
            className="mt-6 md:mt-8 flex gap-3 md:gap-4 overflow-x-auto scroll-smooth pb-3 -mx-6 md:mx-0 px-6 md:px-0"
            data-testid="gallery-thumbstrip"
            style={{ scrollbarWidth: "thin" }}
          >
            {GALLERY_DOORS.map((d, idx) => {
              const isActive = idx === active;
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => jumpTo(idx)}
                  data-thumb-index={idx}
                  data-testid={`gallery-thumb-${d.id}`}
                  aria-label={`Show ${d.name}`}
                  aria-current={isActive ? "true" : "false"}
                  className={`relative flex-shrink-0 w-24 md:w-32 aspect-[4/3] overflow-hidden transition-all duration-300 ${
                    isActive
                      ? "ring-2 ring-brand-gold opacity-100"
                      : "opacity-60 hover:opacity-100"
                  }`}
                >
                  <img
                    src={d.image}
                    alt={d.name}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                  {isActive && (
                    <span className="absolute inset-x-0 bottom-0 h-[3px] bg-brand-gold" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
